import React, { useState } from 'react';
import {
  CustomerReports,
  VendorReports,
  BankReports,
  AdvancedAnalytics
} from '../components/reports';

type ReportTab = 'customers' | 'vendors' | 'banks' | 'analytics';

const T = {
  bg: 'transparent',
  card: '#ffffff',
  elevated: '#f4efe2',
  gold: '#d97706',
  goldLight: '#d97706',
  goldHover: '#b45309',
  textPrimary: '#1c2033',
  textSecondary: '#5b5a52',
  textMuted: '#6b6a5c',
  textDim: '#e3dcc4',
  border: 'rgba(92,78,51,0.15)',
  borderSubtle: 'rgba(92,78,51,0.25)',
};

export const ReportsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ReportTab>('customers');
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);

  const tabs = [
    { id: 'customers', label: 'Customer Reports', icon: '👥' },
    { id: 'vendors', label: 'Vendor Reports', icon: '🏪' },
    { id: 'banks', label: 'Bank Reports', icon: '🏦' },
    { id: 'analytics', label: 'Advanced Analytics', icon: '📊' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'customers':
        return <CustomerReports />;
      case 'vendors':
        return <VendorReports />;
      case 'banks':
        return <BankReports />;
      case 'analytics':
        return <AdvancedAnalytics />;
      default:
        return <CustomerReports />;
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 32,
          fontWeight: 700,
          color: T.textPrimary,
          margin: 0,
          letterSpacing: '0.02em',
        }}>
          Comprehensive Reports
        </h1>
        <p style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 14,
          color: T.textMuted,
          marginTop: 6,
          marginBottom: 0,
        }}>
          Generate detailed reports and analytics for customers, vendors, and bank accounts
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{
        borderBottom: `1px solid ${T.border}`,
        marginBottom: 24,
      }}>
        <nav style={{ display: 'flex', gap: 0 }}>
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const isHovered = hoveredTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as ReportTab)}
                onMouseEnter={() => setHoveredTab(tab.id)}
                onMouseLeave={() => setHoveredTab(null)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: isActive
                    ? `2px solid ${T.gold}`
                    : `2px solid transparent`,
                  background: 'transparent',
                  cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 9,
                  fontWeight: 600,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: isActive
                    ? T.gold
                    : isHovered
                    ? T.textSecondary
                    : T.textMuted,
                  whiteSpace: 'nowrap',
                  transition: 'color 0.2s, border-color 0.2s',
                  marginBottom: -1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 13 }}>{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 8,
      }}>
        {renderTabContent()}
      </div>
    </div>
  );
};
